/**
 * Thin wrapper around Google Analytics 4 (gtag.js).
 *
 * The module exposes three things:
 *  - `initAnalytics()` — call once at app boot to inject the gtag script
 *    and initialise the dataLayer. Idempotent: subsequent calls no-op.
 *  - `track(event, params)` — fire a custom event (e.g. `feature_opened`).
 *  - `trackPageView(path, title)` — fire a manual `page_view`. Used by
 *    the route-change listener since GA's automatic page_view doesn't
 *    catch SPA navigations.
 *
 * All three are safe to call before gtag.js has finished loading — the
 * queued `dataLayer.push` calls are flushed once the script attaches.
 * If `VITE_GA_MEASUREMENT_ID` is empty, every function no-ops.
 */
import { env } from './env';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __OLLIE_GA_BOOTSTRAPPED__?: boolean;
  }
}

const MEASUREMENT_ID = env.gaMeasurementId;

export function initAnalytics(): void {
  if (typeof window === 'undefined' || !MEASUREMENT_ID) return;
  if (window.__OLLIE_GA_BOOTSTRAPPED__) return;
  window.__OLLIE_GA_BOOTSTRAPPED__ = true;

  window.dataLayer = window.dataLayer || [];
  // Use `arguments` so dataLayer entries match gtag.js's expected shape
  // exactly — the official snippet does the same.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // `send_page_view: false` — we fire page_view manually on route change
  // (see `useAnalyticsRouteTracker`) so we get one event per real
  // navigation instead of zero (SPA) or duplicates.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}

export function track(event: string, params?: Record<string, unknown>): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', event, params ?? {});
}

export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

/** Attach an account id to every subsequent event, for user-level reports. */
export function setAnalyticsUser(userId: string | null): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('config', MEASUREMENT_ID, { user_id: userId ?? undefined });
}
